import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  ALLERGY_REACTION_SEVERITIES,
  formatEatTime,
  VITAL_SIGNS,
  type AllergyReactionSeverity,
} from '@wodi/domain';
import type { ObservationView, TriageView } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  RadioGroup,
  Section,
  Tag,
  TextField,
  VITAL_FLAG_WORDS,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  clinicalRulesQuery,
  correctObservation,
  enterInError,
  recordAllergy,
  recordNoKnownAllergies,
  triageQuery,
  type EnteredInErrorKind,
} from '../api/triage.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { TriageForm } from './triage-form.tsx';
import styles from './triage.module.css';

// The triage work area for one visit (plan §6.3): allergies, then either the triage form
// or, once triaged, the current assessment with its vitals, corrections and history.

const SEVERITY_LABELS: Record<AllergyReactionSeverity, string> = {
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
};

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function AllergySection({ view }: { view: TriageView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const mayRecord = session !== null && can(session, 'allergies.record');
  const [adding, setAdding] = useState(false);
  const [substance, setSubstance] = useState('');
  const [reaction, setReaction] = useState('');
  const [severity, setSeverity] = useState<AllergyReactionSeverity | null>(null);
  const update = (next: TriageView) => {
    queryClient.setQueryData(['visits', view.visitId, 'triage'], next);
    void queryClient.invalidateQueries({ queryKey: ['patients', view.patientId] });
  };
  const nka = useMutation({
    mutationFn: () => recordNoKnownAllergies(view.visitId),
    onSuccess: (next) => {
      update(next);
      toasts.show({ title: 'No known allergies recorded' });
    },
  });
  const add = useMutation({
    mutationFn: () =>
      recordAllergy(view.visitId, {
        substance: substance.trim(),
        ...(reaction.trim() === '' ? {} : { reaction: reaction.trim() }),
        ...(severity === null ? {} : { severity }),
      }),
    onSuccess: (next) => {
      update(next);
      setAdding(false);
      setSubstance('');
      setReaction('');
      setSeverity(null);
      toasts.show({ title: 'Allergy recorded' });
    },
  });
  const current = view.allergies;
  const error = nka.error ?? add.error;

  return (
    <Section title="Allergies">
      {current.state === 'list' ? (
        <ul className={styles.allergyList}>
          {current.allergies.map((a) => (
            <li key={a.id}>
              <Tag state="danger">
                {a.reaction === null ? a.substance : `${a.substance} (${a.reaction})`}
              </Tag>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.allergyState}>
          {current.state === 'no_known_allergies' ? 'No known allergies' : 'Allergies not recorded'}
        </p>
      )}
      {!mayRecord ? (
        <p className={styles.help}>Only nurses and clinicians can record allergies.</p>
      ) : (
        <div className={styles.inlineActions}>
          <Button
            dense
            onPress={() => {
              setAdding(true);
            }}
          >
            Add allergy
          </Button>
          {current.state === 'not_recorded' ? (
            <Button
              dense
              isPending={nka.isPending}
              pendingLabel="Recording…"
              onPress={() => {
                nka.mutate();
              }}
            >
              No known allergies
            </Button>
          ) : null}
        </div>
      )}
      {adding ? (
        <div className={styles.allergyForm}>
          <TextField label="Substance" value={substance} onChange={setSubstance} autoFocus />
          <TextField label="Reaction" value={reaction} onChange={setReaction} />
          <RadioGroup
            label="Severity"
            orientation="horizontal"
            value={severity}
            onChange={(value) => {
              setSeverity(value as AllergyReactionSeverity);
            }}
            options={ALLERGY_REACTION_SEVERITIES.map((s) => ({ id: s, label: SEVERITY_LABELS[s] }))}
          />
          <p className={styles.help}>
            Recorded as reported by the patient, for the clinician to confirm.
          </p>
          <div className={styles.inlineActions}>
            <Button
              dense
              variant="primary"
              isDisabled={substance.trim() === ''}
              isPending={add.isPending}
              pendingLabel="Recording…"
              onPress={() => {
                add.mutate();
              }}
            >
              Record allergy
            </Button>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                setAdding(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {error === null ? null : (
        <Notice tone="error">
          {errorText(error, 'The allergy could not be recorded. Try again.')}
        </Notice>
      )}
    </Section>
  );
}

function flagWord(o: ObservationView): string | null {
  if (o.interpretation === 'normal') return null;
  const word = VITAL_FLAG_WORDS[o.interpretation];
  return o.rule?.approval === 'provisional' && word !== null ? `${word} (provisional)` : word;
}

function ObservationRow({
  o,
  onCorrect,
  onError,
}: {
  o: ObservationView;
  onCorrect: (o: ObservationView) => void;
  onError: (o: ObservationView) => void;
}) {
  const label = VITAL_SIGNS[o.key].label;
  const flag = flagWord(o);
  const current = o.status === 'final';
  const critical = o.interpretation === 'critical_low' || o.interpretation === 'critical_high';
  return (
    <tr className={current ? undefined : styles.superseded}>
      <th scope="row">{label}</th>
      <td
        className={styles.value}
        data-abnormal={(current && flag !== null && o.interpretation !== 'no_range') || undefined}
        data-critical={(current && critical) || undefined}
      >
        {`${o.value} ${o.unit.display}`}
        {o.context === null ? '' : ` (${o.context})`}
      </td>
      <td>{current ? (flag ?? '') : o.status === 'amended' ? 'Corrected' : 'Entered in error'}</td>
      <td>
        {current ? (
          <span className={styles.inlineActions}>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                onCorrect(o);
              }}
            >
              Correct
            </Button>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                onError(o);
              }}
            >
              Entered in error
            </Button>
          </span>
        ) : (
          (o.statusReason ?? '')
        )}
      </td>
    </tr>
  );
}

function TriageSummary({ view, onRetriage }: { view: TriageView; onRetriage: () => void }) {
  const queryClient = useQueryClient();
  const assessment = view.assessment;
  const [correcting, setCorrecting] = useState<ObservationView | null>(null);
  const [newValue, setNewValue] = useState('');
  const [erroring, setErroring] = useState<{
    kind: EnteredInErrorKind;
    id: string;
    what: string;
  } | null>(null);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['visits', view.visitId, 'triage'] });
  const correct = useMutation({
    mutationFn: (reason: string) => {
      if (correcting === null) throw new Error('nothing to correct');
      return correctObservation(correcting.id, newValue.trim(), reason);
    },
    onSuccess: async () => {
      setCorrecting(null);
      setNewValue('');
      await refresh();
      toasts.show({ title: 'Value corrected' });
    },
  });
  const markError = useMutation({
    mutationFn: (reason: string) => {
      if (erroring === null) throw new Error('nothing to mark');
      return enterInError(erroring.kind, erroring.id, reason);
    },
    onSuccess: async () => {
      setErroring(null);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['queues'] });
      toasts.show({ title: 'Marked as entered in error' });
    },
  });
  const set = view.vitalSets.find((s) => s.status === 'active');

  return (
    <Section title="Triage">
      {assessment === null ? (
        <p>Not triaged yet.</p>
      ) : (
        <dl className={styles.summary}>
          <dt>Category</dt>
          <dd>
            {assessment.category.provisional
              ? `${assessment.category.display} (provisional)`
              : assessment.category.display}
          </dd>
          <dt>Chief complaint</dt>
          <dd>{assessment.chiefComplaint}</dd>
          <dt>Recorded</dt>
          <dd>{`${formatEatTime(new Date(assessment.assessedAt))} by ${assessment.recordedBy.displayName}`}</dd>
        </dl>
      )}
      {set === undefined ? (
        <p className={styles.help}>No vitals recorded for this visit.</p>
      ) : (
        <table className={styles.vitalsTable}>
          <caption className={styles.caption}>
            {`Vitals measured at ${formatEatTime(new Date(set.effectiveAt))}`}
            {set.bmi === null ? '' : `, BMI ${set.bmi}`}
          </caption>
          <tbody>
            {set.observations.map((o) => (
              <ObservationRow
                key={o.id}
                o={o}
                onCorrect={(target) => {
                  setCorrecting(target);
                  setNewValue(target.value);
                }}
                onError={(target) => {
                  setErroring({
                    kind: 'observations',
                    id: target.id,
                    what: VITAL_SIGNS[target.key].label.toLowerCase(),
                  });
                }}
              />
            ))}
          </tbody>
        </table>
      )}
      <div className={styles.inlineActions}>
        <Button onPress={onRetriage}>Re-triage</Button>
        {assessment === null ? null : (
          <Button
            variant="quiet"
            onPress={() => {
              setErroring({ kind: 'triage-assessments', id: assessment.id, what: 'triage' });
            }}
          >
            Triage entered in error
          </Button>
        )}
      </div>
      {view.previousAssessments.length === 0 ? null : (
        <details className={styles.history}>
          <summary>{`Earlier triage (${String(view.previousAssessments.length)})`}</summary>
          <ul>
            {view.previousAssessments.map((a) => (
              <li key={a.id}>
                {`${formatEatTime(new Date(a.assessedAt))}: ${a.category.display}, ${
                  a.status === 'superseded'
                    ? 'replaced by a later triage'
                    : `entered in error (${a.statusReason ?? ''})`
                }`}
              </li>
            ))}
          </ul>
        </details>
      )}
      <ReasonDialog
        title={`Correct ${correcting === null ? 'the value' : VITAL_SIGNS[correcting.key].label.toLowerCase()}`}
        isOpen={correcting !== null}
        onOpenChange={(open) => {
          if (!open) setCorrecting(null);
        }}
        confirmLabel="Correct value"
        pendingLabel="Correcting…"
        isPending={correct.isPending}
        error={
          correct.error === null
            ? null
            : errorText(correct.error, 'The value could not be corrected.')
        }
        onConfirm={(reason) => {
          correct.mutate(reason);
        }}
      >
        {correcting === null ? null : (
          <TextField
            label={`Correct ${VITAL_SIGNS[correcting.key].label.toLowerCase()}`}
            value={newValue}
            onChange={setNewValue}
            unit={VITAL_SIGNS[correcting.key].unitDisplay}
            description="The old value stays in the history with your reason."
          />
        )}
      </ReasonDialog>
      <ReasonDialog
        title={`Mark ${erroring?.what ?? 'this'} as entered in error`}
        isOpen={erroring !== null}
        onOpenChange={(open) => {
          if (!open) setErroring(null);
        }}
        confirmLabel="Mark as entered in error"
        pendingLabel="Marking…"
        isPending={markError.isPending}
        error={
          markError.error === null ? null : errorText(markError.error, 'It could not be marked.')
        }
        onConfirm={(reason) => {
          markError.mutate(reason);
        }}
      >
        <p>Nothing is deleted: the record stays in the history, marked with your reason.</p>
      </ReasonDialog>
    </Section>
  );
}

/** Allergies and triage for one visit, with loading and error states. */
export function TriagePanel({ visitId, onSaved }: { visitId: string; onSaved: () => void }) {
  const view = useQuery(triageQuery(visitId));
  const rules = useQuery(clinicalRulesQuery);
  const [retriage, setRetriage] = useState(false);

  if (view.isPending || rules.isPending) return <LoadingBlock label="Loading triage" rows={6} />;
  if (view.isError || rules.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button
            dense
            onPress={() => {
              void view.refetch();
              void rules.refetch();
            }}
          >
            Try again
          </Button>
        }
      >
        {errorText(view.error ?? rules.error, 'Triage could not be loaded. Try again.')}
      </Notice>
    );
  }
  const data = view.data;
  const showForm = data.visitStatus === 'open' && (data.assessment === null || retriage);
  return (
    <div className={styles.panel}>
      <AllergySection view={data} />
      {showForm ? (
        <TriageForm
          key={retriage ? 'retriage' : 'first'}
          view={data}
          rules={rules.data}
          onSaved={() => {
            setRetriage(false);
            toasts.show({ title: 'Triage saved' });
            onSaved();
          }}
          {...(data.assessment === null
            ? {}
            : {
                onCancel: () => {
                  setRetriage(false);
                },
              })}
        />
      ) : (
        <TriageSummary
          view={data}
          onRetriage={() => {
            setRetriage(true);
          }}
        />
      )}
    </div>
  );
}
